import React, { useContext } from 'react';
import { Formik, Form, Field } from 'formik';
import { Button } from 'reactstrap';
import * as Yup from 'yup'; // For validation
import { FaCheck } from 'react-icons/fa6';
import FormikInputField from 'components/Fields/FormikInputField';
import LoadingButton from 'components/common/LoadingButton';
import { axiosUpdate } from 'services';
import { authContext } from 'contexts/AuthContext';
import { toast } from 'react-toastify';

// Validation schema
const RateManagementSchema = Yup.object().shape({
  isRateVisible: Yup.boolean(),
  markupPercentage: Yup.number()
    .typeError('Markup must be a number')
    .positive('Markup must be positive')
    .max(100, 'Markup cannot be more than 100')
    .min(0, 'Markup cannot be less than 0')
    .required('Markup is required'),
});

const visibilityToggle = ({ field, form }) => {
  return (
    <div className="toggle-check cmn-toggle d-flex justify-content-center">
      <div className="d-inline-block position-relative">
        <input
          type="checkbox"
          onChange={() => form.setFieldValue(field.name, !field.value)}
          checked={field.value}
          id={field.name}
        />
        <FaCheck size={10} />
      </div>
    </div>
  );
};

// The parent of this component will call the API for the initial markup percentage and visibility state.
const AdvanceControls = ({
  isRateVisible,
  markupPercentage,
  onDiscard,
  afterSubmitFetch,
  appSettingId = 13 /* dummy value*/,
}) => {
  const initialValues = {
    isRateVisible: isRateVisible === 'TRUE',
    markupPercentage: markupPercentage || 12,
  };

  const { auth } = useContext(authContext);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);
      const data = {
        id: appSettingId,
        type: 'ADVANCE_CONTROLS',
        values: [
          {
            key: 'RATE_VISIBILITY',
            value: values.isRateVisible ? 'TRUE' : 'FALSE',
            type: 'VALUE',
          },
          {
            key: 'MARKUP_PERCENT',
            value: values.markupPercentage.toString(),
            type: 'PERCENTAGE',
          },
        ],
      };
      const response = await axiosUpdate(
        `${process.env.REACT_APP_REST_API_ENDPOINT}common/update-app-setting`,
        data,
        auth?.data?.token,
      );

      if (response.isSuccessful) {
        toast.success('Settings updated successfully');
      } else {
        toast.error('Failed to update settings');
      }

      await afterSubmitFetch();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setSubmitting(false);
      onDiscard(); // Call the discard function to close the modal
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={RateManagementSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, isValid, dirty, isSubmitting }) => (
        <Form className="advance-controls-container">
          <div className="item">
            {/* left text */}
            <div className="left">
              <span className="title">Manage Rate Visibility</span>
              <span className="description">
                Control the visibility of all rate cards across routes.
              </span>
            </div>
            {/* right switch */}
            <div className="right">
              <Field name="isRateVisible">
                {({ field, form }) => visibilityToggle({ field, form })}
              </Field>
            </div>
          </div>
          <div className="item">
            {/* left text */}
            <div className="left">
              <span className="title">Markup %</span>
              <span className="description">
                Apply a standard markup % on fallback base rates{' '}
              </span>
            </div>
            {/* right field */}
            <div className="right">
              <Field
                id="idMarkupPercentage"
                name="markupPercentage"
                label=""
                type="text"
                notShowError
                showPercentage
                component={FormikInputField}
              />
            </div>
          </div>
          {errors.markupPercentage && (
            <div className="text-danger">{errors.markupPercentage}</div>
          )}
          {/* footer with discard and save buttons */}
          <div className="text-end mt-3 mb-1">
            <Button
              outline
              onClick={onDiscard}
              color="secondary"
              type="button"
              size="sm"
              className="me-2 mw-100"
            >
              Discard
            </Button>
            <LoadingButton
              className="mw-100 loading-button"
              loading={isSubmitting}
              disabled={!isValid || !dirty || isSubmitting}
              color="warning"
              size="sm"
              type="submit"
            >
              Save
            </LoadingButton>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default AdvanceControls;
